// The floating call pill (presentational). State and data come from
// useCallPill via CallPillHost; this file only draws and handles dragging.
import PropTypes from 'prop-types';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Badge, Box, Button, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import Iconify from '../Iconify';
import { voiceCallSummary } from '../../sections/notifications/voiceCallScreenPop';
import {
    ACTION, CALL_STATE, actionsFor, callerDisplay, formatDuration, statusLine, timerStart,
} from './callPillModel';
import { PILL_MARGIN, pixelPosition, snapToEdge } from './callPillPosition';
import { callLayer } from './callLayers';
import ImOnThisCallButton from '../teamMember/ImOnThisCallButton';
import { canClaimOnPill, pillRowId } from '../teamMember/teamMemberModel';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
`;

const DRAG_THRESHOLD_PX = 4;
const DOT_SIZE = { width: 52, height: 52 };

const STATE_STYLE = {
    [CALL_STATE.RINGING]: { bg: 'grey.900', dot: 'success.main', icon: 'mdi:phone-ring' },
    [CALL_STATE.ON_CALL]: { bg: 'grey.900', dot: 'success.main', icon: 'mdi:phone-in-talk' },
    [CALL_STATE.HELD]: { bg: 'grey.900', dot: 'warning.main', icon: 'mdi:phone-paused' },
    [CALL_STATE.ANSWERED_ELSEWHERE]: { bg: 'grey.800', dot: 'grey.500', icon: 'mdi:phone-forward' },
    [CALL_STATE.ENDED]: { bg: 'grey.800', dot: 'grey.500', icon: 'mdi:phone-hangup' },
    [CALL_STATE.MISSED]: { bg: 'error.dark', dot: 'error.light', icon: 'mdi:phone-missed' },
};

// The pill rests IN the app header's own band, not under it: its top edge lines up
// with the search box and the practice button, which is what "at the top" means to
// the person looking at it. Measured on a real screen, docking under the header left
// a 64 px strip of table rows above the pill and it read as floating over the list.
//
// It can sit there because the header is empty across the middle -- search on the
// left, the practice button and icons on the right -- and because the pill already
// stacks above the bar (callLayers: pill 1401, app bar ~1101), so nothing is hidden.
// headerInset stays for the sign-in desk prompt, which is a wide banner and would
// cover the search box if it moved up here.
const viewport = () => ({ width: window.innerWidth, height: window.innerHeight, top: PILL_MARGIN });
const sameViewport = (a, b) => a.width === b.width && a.height === b.height && a.top === b.top;
// How often the pill checks where the page header ends (pages change without a resize).
const VIEWPORT_RECHECK_MS = 1000;
// One size whatever the call says (CU-869fmcc9r): long names and statuses are cut short,
// the timer never wraps, the buttons never move.
const PILL_WIDTH = 'min(560px, calc(100vw - 24px))';
const PILL_HEIGHT = 64;
// Answered at another desk: a passive, smaller pill (who, "I'm on this call", close).
export const ELSEWHERE_PILL_WIDTH = 'min(420px, calc(100vw - 24px))';
export const ELSEWHERE_PILL_HEIGHT = 52;

// Ticks once a second only while a timer is on screen.
function useNow(enabled) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!enabled) return undefined;
        setNow(Date.now());
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [enabled]);
    return now;
}

// Pointer drag that snaps to the nearest edge on release. Returns the live
// pixel position plus handlers; a press that moves less than the threshold is
// left to the click handlers underneath.
function useEdgeDrag({ position, onPositionChange, size }) {
    const [drag, setDrag] = useState(null);
    const [vp, setVp] = useState(viewport);
    const startRef = useRef(null);
    const dragRef = useRef(null);
    // The click that ends a drag must not also trigger the element's onClick.
    const justDraggedRef = useRef(false);

    // Resize, scroll and page changes move the header's bottom edge: follow it.
    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            const next = viewport();
            setVp((prev) => (sameViewport(prev, next) ? prev : next));
        };
        const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
        window.addEventListener('resize', schedule);
        window.addEventListener('scroll', schedule, { capture: true, passive: true });
        const timer = setInterval(schedule, VIEWPORT_RECHECK_MS);
        return () => {
            window.removeEventListener('resize', schedule);
            window.removeEventListener('scroll', schedule, { capture: true });
            clearInterval(timer);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    const resting = pixelPosition(position, vp, size);

    const onPointerDown = useCallback((e) => {
        if (e.button !== undefined && e.button !== 0) return;
        if (e.target.closest?.('button')) return;
        startRef.current = { x: e.clientX, y: e.clientY, left: resting.left, top: resting.top, moved: false, id: e.pointerId };
    }, [resting.left, resting.top]);

    useEffect(() => {
        const onMove = (e) => {
            const s = startRef.current;
            if (!s || (s.id !== undefined && e.pointerId !== undefined && e.pointerId !== s.id)) return;
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
            s.moved = true;
            dragRef.current = { left: s.left + dx, top: s.top + dy };
            setDrag(dragRef.current);
        };
        const onUp = () => {
            const s = startRef.current;
            startRef.current = null;
            const d = dragRef.current;
            dragRef.current = null;
            if (!s?.moved || !d) return;
            justDraggedRef.current = true;
            setTimeout(() => { justDraggedRef.current = false; }, 0);
            setDrag(null);
            onPositionChange(snapToEdge({ x: d.left + size.width / 2, y: d.top + size.height / 2 }, viewport()));
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, [onPositionChange, size.width, size.height]);

    const consumeDrag = useCallback(() => justDraggedRef.current, []);
    return { pos: drag || resting, dragging: Boolean(drag), onPointerDown, consumeDrag };
}

const shellSx = (pos, dragging) => ({
    position: 'fixed',
    left: pos.left,
    top: pos.top,
    zIndex: callLayer('PILL'),
    touchAction: 'none',
    userSelect: 'none',
    cursor: dragging ? 'grabbing' : 'grab',
    transition: dragging ? 'none' : 'left 180ms ease-out, top 180ms ease-out, opacity 600ms ease-in',
});

// The round bubble: the minimised pill, or calls tucked away by "Hide for this
// call" / Do not disturb. Draggable, pulses while a call rings, shows how many
// calls it holds; a click expands it (CU-869fmcc9r).
export function CallPillDot({ count, dnd, minimised, ringing, position, onPositionChange, onClick }) {
    const { pos, dragging, onPointerDown, consumeDrag } = useEdgeDrag({ position, onPositionChange, size: DOT_SIZE });
    const calls = `${count} call${count === 1 ? '' : 's'}`;
    let label = `${count} hidden call${count === 1 ? '' : 's'} · show`;
    if (dnd) label = `Do not disturb is on · ${calls}`;
    else if (minimised) label = `${ringing ? 'Call ringing' : calls} · click to expand`;
    return (
        <Tooltip title={label}>
            <Box
                data-testid="call-pill-dot"
                role="button"
                aria-label={label}
                tabIndex={0}
                onPointerDown={onPointerDown}
                onClick={() => { if (!consumeDrag()) onClick(); }}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    onClick();
                }}
                data-ringing={ringing ? 'true' : 'false'}
                sx={{
                    ...shellSx(pos, dragging),
                    ...DOT_SIZE,
                    borderRadius: '50%',
                    bgcolor: (dnd && 'grey.700') || (ringing && 'success.main') || 'grey.900',
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 6,
                    animation: ringing && !dnd ? `${pulse} 1.4s infinite` : 'none',
                }}
            >
                <Badge
                    badgeContent={count}
                    color="error"
                    invisible={count < 1}
                    overlap="circular"
                    sx={{ '& .MuiBadge-badge': { top: -10, right: -10 } }}
                >
                    <Iconify icon={(dnd && 'mdi:bell-off') || (ringing && 'mdi:phone-ring') || 'mdi:phone'} width={24} />
                </Badge>
            </Box>
        </Tooltip>
    );
}

CallPillDot.propTypes = {
    count: PropTypes.number.isRequired,
    dnd: PropTypes.bool,
    minimised: PropTypes.bool,
    ringing: PropTypes.bool,
    position: PropTypes.object.isRequired,
    onPositionChange: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
};

// The pill's ⋮ menu: Hide for this call · Do not disturb · Change desk · Link
// to existing patient (CU-869fmcc9r). Items the host doesn't offer are left out.
function PillMenu({ call, dnd, sound, onHide, onToggleDnd, onToggleSound, onChangeDesk, onLinkLead }) {
    const [anchor, setAnchor] = useState(null);
    const close = () => setAnchor(null);
    const run = (fn) => () => { close(); fn(); };
    const closable = call.state === CALL_STATE.ENDED || call.state === CALL_STATE.MISSED;
    return (
        <>
            <Tooltip title="More">
                <IconButton
                    size="small"
                    color="inherit"
                    onClick={(e) => setAnchor(e.currentTarget)}
                    aria-label="More call options"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(anchor)}
                    data-testid="call-pill-menu"
                >
                    <Iconify icon="mdi:dots-vertical" width={18} />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} sx={{ zIndex: callLayer('POPUP') }}>
                {onLinkLead && (
                    <MenuItem onClick={run(() => onLinkLead(call))} data-testid="call-pill-menu-link">
                        <ListItemIcon><Iconify icon="mdi:account-search-outline" width={18} /></ListItemIcon>
                        <ListItemText>Link to existing patient</ListItemText>
                    </MenuItem>
                )}
                {!closable && (
                    <MenuItem onClick={run(() => onHide(call.callId))} data-testid="call-pill-menu-hide">
                        <ListItemIcon><Iconify icon="mdi:eye-off-outline" width={18} /></ListItemIcon>
                        <ListItemText>Hide for this call</ListItemText>
                    </MenuItem>
                )}
                {onChangeDesk && (
                    <MenuItem onClick={run(onChangeDesk)} data-testid="call-pill-menu-desk">
                        <ListItemIcon><Iconify icon="mdi:deskphone" width={18} /></ListItemIcon>
                        <ListItemText>Change desk</ListItemText>
                    </MenuItem>
                )}
                {(onToggleDnd || onToggleSound) && <Divider />}
                {onToggleSound && (
                    <MenuItem
                        role="menuitemcheckbox"
                        aria-checked={Boolean(sound)}
                        onClick={run(() => onToggleSound(!sound))}
                        data-testid="call-pill-menu-sound"
                    >
                        <ListItemText>Call sound</ListItemText>
                        <Switch size="small" checked={Boolean(sound)} tabIndex={-1} sx={{ ml: 1, pointerEvents: 'none' }} inputProps={{ 'aria-hidden': true }} />
                    </MenuItem>
                )}
                {onToggleDnd && (
                    <MenuItem
                        role="menuitemcheckbox"
                        aria-checked={Boolean(dnd)}
                        onClick={run(() => onToggleDnd(!dnd))}
                        data-testid="call-pill-menu-dnd"
                    >
                        <ListItemText>Do not disturb</ListItemText>
                        <Switch size="small" checked={Boolean(dnd)} tabIndex={-1} sx={{ ml: 1, pointerEvents: 'none' }} inputProps={{ 'aria-hidden': true }} />
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

PillMenu.propTypes = {
    call: PropTypes.object.isRequired,
    dnd: PropTypes.bool,
    sound: PropTypes.bool,
    onHide: PropTypes.func.isRequired,
    onToggleDnd: PropTypes.func,
    onToggleSound: PropTypes.func,
    onChangeDesk: PropTypes.func,
    onLinkLead: PropTypes.func,
};

// The one-time "Enable call sound" question (CU-869fmcc9r): a small toast hanging under
// the pill, never over it or the page header; it goes once answered.
function SoundPrompt({ onEnable, onDismiss, above }) {
    return (
        <Box
            data-testid="call-sound-prompt"
            role="dialog"
            aria-label="Call sound"
            // Not a drag handle: clicks here are for its buttons.
            onPointerDown={(e) => e.stopPropagation()}
            sx={{
                position: 'absolute',
                // Under the pill; above it when the pill sits at the bottom of the screen.
                ...(above ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'nowrap',
                maxWidth: 'calc(100vw - 24px)',
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 6,
                cursor: 'default',
            }}
        >
            <Iconify icon="mdi:volume-high" width={18} />
            <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>Hear a short ring for calls?</Typography>
            <Button size="small" variant="contained" onClick={onEnable} data-testid="call-sound-enable" sx={{ flexShrink: 0 }}>
                Enable call sound
            </Button>
            <Button size="small" color="inherit" onClick={onDismiss} data-testid="call-sound-dismiss" sx={{ flexShrink: 0 }}>
                Not now
            </Button>
        </Box>
    );
}

SoundPrompt.propTypes = { onEnable: PropTypes.func.isRequired, onDismiss: PropTypes.func.isRequired, above: PropTypes.bool };

export default function CallPill({
    call, others, hiddenCount, position, onPositionChange, onAction, onReview, onHide, onClose, onShowHidden, onShowOthers, controls,
    deskName, isAdmin, dnd, sound, onMinimise, onToggleDnd, onToggleSound, onChangeDesk, onLinkLead, soundPrompt, stageLabel, claimPracticeId, claimPersonId,
}) {
    const ref = useRef(null);
    const [size, setSize] = useState({ width: 360, height: 64 });
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const { offsetWidth: width, offsetHeight: height } = el;
        if (width && height && (width !== size.width || height !== size.height)) setSize({ width, height });
    });
    const { pos, dragging, onPointerDown } = useEdgeDrag({ position, onPositionChange, size });

    const start = timerStart(call);
    const now = useNow(Boolean(start));
    const style = STATE_STYLE[call.state] || STATE_STYLE[CALL_STATE.ENDED];
    const { name, number, label } = callerDisplay(call);
    // The answered-elsewhere pill is passive: no Create lead / Open patient, no menu, no call controls.
    const elsewhereNow = call.state === CALL_STATE.ANSWERED_ELSEWHERE;
    const actions = elsewhereNow ? [] : actionsFor(call);
    const closable = call.state === CALL_STATE.ENDED || call.state === CALL_STATE.MISSED;
    // Answered at another desk: a compact pill that stays (no fade) with "I'm on this call" and a close button.
    const elsewhere = call.state === CALL_STATE.ANSWERED_ELSEWHERE;
    const ringingHere = call.state === CALL_STATE.RINGING && call.direction !== 'outbound';
    // The patient summary the old toast showed (status, appointment, tasks…).
    const summary = call.lead?.id ? voiceCallSummary({ lead_summary: call.leadSummary }) : '';
    const subline = [label, name ? number : ''].filter(Boolean).join(' · ');

    return (
        <Box
            ref={ref}
            data-testid="call-pill"
            data-state={call.state}
            role="group"
            aria-label="Live call"
            onPointerDown={onPointerDown}
            sx={{
                ...shellSx(pos, dragging),
                width: elsewhereNow ? ELSEWHERE_PILL_WIDTH : PILL_WIDTH,
                height: elsewhereNow ? ELSEWHERE_PILL_HEIGHT : PILL_HEIGHT,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                borderRadius: 4,
                bgcolor: style.bg,
                color: 'common.white',
                boxShadow: 8,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%', minWidth: 0 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: style.dot,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: call.state === CALL_STATE.RINGING ? `${pulse} 1.4s infinite` : 'none',
                    }}
                >
                    <Iconify icon={style.icon} width={20} />
                </Box>
                {/* Two rows, never more: who (with the patient summary on hover), then the
                    status and timer. Long text is cut short; the timer never wraps. */}
                <Tooltip title={summary ? summary.charAt(0).toUpperCase() + summary.slice(1) : ''} placement="bottom-start">
                    <Box sx={{ minWidth: 0, flex: 1 }} data-testid="call-pill-summary" data-summary={summary || undefined}>
                        <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap sx={{ flexShrink: 0, maxWidth: '60%' }} data-testid="call-pill-caller">
                                {name || number}
                            </Typography>
                            {subline && (
                                <Typography variant="caption" noWrap sx={{ opacity: 0.7, minWidth: 0 }} data-testid="call-pill-label">
                                    {subline}
                                </Typography>
                            )}
                            {isAdmin && call.notLinked && (
                                <Tooltip title="No desk on this call is linked to a person, so everyone with Call Logs was told. DentalSEM staff only.">
                                    <Typography variant="caption" noWrap sx={{ opacity: 0.6, fontSize: 11, flexShrink: 0 }} data-testid="call-pill-admin-hint">
                                        Not linked
                                    </Typography>
                                </Tooltip>
                            )}
                        </Stack>
                        <Stack
                            direction="row"
                            alignItems="baseline"
                            sx={{ minWidth: 0, opacity: ringingHere ? 1 : 0.85, fontWeight: ringingHere ? 600 : 400 }}
                            data-testid="call-pill-status"
                        >
                            {/* Only the state is announced; the ticking timer is not. There is no
                                Answer button: staff pick up the handset, and the pill follows. */}
                            <Typography variant="caption" noWrap role="status" aria-live="polite" sx={{ fontWeight: 'inherit', minWidth: 0 }}>
                                {elsewhere && call.answeredByDesk ? `Answered at ${call.answeredByDesk}` : statusLine(call, { deskName, isAdmin })}
                            </Typography>
                            {start ? (
                                <Typography
                                    variant="caption"
                                    aria-hidden="true"
                                    sx={{ fontWeight: 'inherit', flexShrink: 0, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                                    data-testid="call-pill-timer"
                                >
                                    {`\u00a0\u00b7 ${formatDuration(now - start)}`}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Box>
                </Tooltip>
                {others > 0 && (
                    <Tooltip title={`${others} more call${others === 1 ? '' : 's'} \u00b7 show the next one`}>
                        {/* A real button, so it is clickable and the drag handler lets it through
                            (useEdgeDrag ignores a press that starts on a button). */}
                        <Button
                            size="small"
                            color="inherit"
                            onClick={() => onShowOthers?.()}
                            aria-label={`Show the next of ${others + 1} calls`}
                            data-testid="call-pill-others"
                            sx={{
                                minWidth: 0,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                flexShrink: 0,
                                color: 'common.white',
                                bgcolor: 'rgba(255,255,255,0.16)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
                            }}
                        >
                            <Typography variant="caption">+{others}</Typography>
                        </Button>
                    </Tooltip>
                )}
                {hiddenCount > 0 && (
                    <Tooltip title={`${hiddenCount} hidden call${hiddenCount === 1 ? '' : 's'} · show`}>
                        <Button
                            size="small"
                            color="inherit"
                            onClick={onShowHidden}
                            data-testid="call-pill-hidden-count"
                            sx={{ minWidth: 0, px: 0.75, borderRadius: 999, flexShrink: 0 }}
                        >
                            <Iconify icon="mdi:eye-off-outline" width={14} />
                            <Typography variant="caption" sx={{ ml: 0.25 }}>{hiddenCount}</Typography>
                        </Button>
                    </Tooltip>
                )}
                {actions.map((a) => (
                    <Button
                        key={a.id}
                        size="small"
                        variant={a.primary ? 'contained' : 'outlined'}
                        color="inherit"
                        onClick={() => onAction(a.id, call)}
                        data-testid={`call-pill-action-${a.id}`}
                        sx={{
                            flexShrink: 0,
                            borderRadius: 999,
                            color: a.primary ? 'grey.900' : 'common.white',
                            bgcolor: a.primary ? 'common.white' : 'transparent',
                            borderColor: 'rgba(255,255,255,0.4)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.85)', color: 'grey.900' },
                        }}
                    >
                        {/* A caller we know is a patient at a stage: the button says what that
                            stage's own table says ("Record Attended", "Follow up"), so it opens
                            something staff already recognise. Without a stage it stays
                            "Open patient" and still only opens the lead page. */}
                        {a.id === ACTION.OPEN_PATIENT && stageLabel ? stageLabel : a.label}
                    </Button>
                ))}
                {/* After the call (ended or missed): the call review, with the conversation and what to do next. */}
                {onReview && closable && (
                    <Button
                        size="small"
                        color="inherit"
                        onClick={() => onReview(call)}
                        data-testid="call-pill-review"
                        sx={{ flexShrink: 0, borderRadius: 999, color: 'common.white', textTransform: 'none !important', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
                    >
                        Review call
                    </Button>
                )}
                {/* Desk-phone calls only: one tap names the caller as the person on this call. */}
                {claimPracticeId && canClaimOnPill(call) && (
                    <ImOnThisCallButton key={call.callId} practiceId={claimPracticeId} callId={pillRowId(call)} mePersonId={claimPersonId} onDark compact />
                )}
                {!elsewhereNow && controls}
                {!elsewhereNow && <PillMenu
                    call={call}
                    dnd={dnd}
                    sound={sound}
                    onHide={onHide}
                    onToggleDnd={onToggleDnd}
                    onToggleSound={onToggleSound}
                    onChangeDesk={onChangeDesk}
                    onLinkLead={onLinkLead}
                />}
                {onMinimise && !closable && !elsewhere && (
                    <Tooltip title="Minimise to a bubble">
                        <IconButton size="small" color="inherit" onClick={onMinimise} aria-label="Minimise call pill" data-testid="call-pill-minimise">
                            <Iconify icon="mdi:window-minimize" width={18} />
                        </IconButton>
                    </Tooltip>
                )}
                {(closable || elsewhere) && (
                    <Tooltip title="Close">
                        <IconButton size="small" color="inherit" onClick={() => onClose(call.callId)} aria-label="Close call pill">
                            <Iconify icon="mdi:close" width={18} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
            {soundPrompt && (
                <SoundPrompt
                    onEnable={soundPrompt.onEnable}
                    onDismiss={soundPrompt.onDismiss}
                    above={pos.top + PILL_HEIGHT + 56 > window.innerHeight}
                />
            )}
        </Box>
    );
}

CallPill.propTypes = {
    call: PropTypes.object.isRequired,
    others: PropTypes.number,
    hiddenCount: PropTypes.number,
    // Call controls (Hold / Transfer / End) supplied by the outbound feature.
    controls: PropTypes.node,
    onShowHidden: PropTypes.func,
    // "+N": show the next of several live calls on the pill.
    onShowOthers: PropTypes.func,
    position: PropTypes.object.isRequired,
    onPositionChange: PropTypes.func.isRequired,
    onAction: PropTypes.func.isRequired,
    // Opens the call review for a call that has ended.
    onReview: PropTypes.func,
    onHide: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    // The desk this user holds ("R TCO 2"), for "Ringing at your desk (R TCO 2)".
    deskName: PropTypes.string,
    // Admins also see internal line names and the "Not linked" hint.
    isAdmin: PropTypes.bool,
    dnd: PropTypes.bool,
    // Call sound on (the short ring), and its switch in the menu.
    sound: PropTypes.bool,
    // { onEnable, onDismiss }: the one-time call sound question under the pill; null once answered.
    soundPrompt: PropTypes.object,
    onToggleSound: PropTypes.func,
    onMinimise: PropTypes.func,
    onToggleDnd: PropTypes.func,
    onChangeDesk: PropTypes.func,
    onLinkLead: PropTypes.func,
    // The name the caller's stage table gives its action button (callPillStage.stageFor).
    stageLabel: PropTypes.string,
    // The practice to record "I'm on this call" against; without it the button is not shown.
    claimPracticeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // The signed-in user's PracticeUser id here (to tell "already you" from "already someone else").
    claimPersonId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
